(() => {
  const sectionSelector = '[data-best-sellers]';
  const trackSelector = '[data-best-sellers-track]';
  const previousSelector = '[data-best-sellers-previous]';
  const nextSelector = '[data-best-sellers-next]';
  const scrollbarThumbSelector = '[data-best-sellers-scrollbar-thumb]';
  const initialized = 'bestSellersInitialized';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const getScrollAmount = (track) => {
    const list = track.querySelector('.best-sellers__track');
    const firstItem = list ? list.firstElementChild : null;

    if (!list || !firstItem) return Math.max(track.clientWidth * 0.5, 240);

    const gap = parseFloat(window.getComputedStyle(list).columnGap) || 0;
    return firstItem.getBoundingClientRect().width + gap;
  };

  const updateButtonState = (track, previousButton, nextButton) => {
    if (!previousButton || !nextButton) return;

    const remaining = track.scrollWidth - track.clientWidth - track.scrollLeft;

    previousButton.disabled = track.scrollLeft <= 2;
    nextButton.disabled = remaining <= 2;
  };

  const updateScrollbarThumb = (track, thumb) => {
    if (!thumb) return;

    const scrollbar = thumb.parentElement;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const scrollbarWidth = scrollbar ? scrollbar.clientWidth : 0;

    if (!scrollbar?.classList.contains('fwn-slider')) {
      const visibleRatio = track.clientWidth / track.scrollWidth;
      const thumbWidthPercent = Math.min(Math.max(visibleRatio * 100, 18), 100);
      const thumbWidthPixels = scrollbarWidth * (thumbWidthPercent / 100);
      const maxOffset = Math.max(scrollbarWidth - thumbWidthPixels, 0);
      const thumbOffset = maxScroll > 0 ? (track.scrollLeft / maxScroll) * maxOffset : 0;

      thumb.style.width = `${thumbWidthPercent}%`;
      thumb.style.transform = `translateX(${thumbOffset}px)`;
      return;
    }

    window.FWNSliders?.setPosition(scrollbar, track.scrollLeft, maxScroll);
  };

  const loadRecommendations = (section, track, onDone) => {
    const url = section.dataset.recommendationsUrl;
    if (!url || section.dataset.recommendationsLoaded === 'true') return;
    section.dataset.recommendationsLoaded = 'true';

    const list = track.querySelector('.best-sellers__track');
    if (!list) return;

    fetch(url)
      .then((response) => (response.ok ? response.text() : Promise.reject(response)))
      .then((markup) => {
        const cards = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelectorAll('.best-sellers__product-card');
        if (!cards.length) return;

        list.querySelectorAll('.best-sellers__product-card').forEach((card) => card.remove());
        list.append(...cards);
        onDone?.();
      })
      .catch(() => {
        // Network/parse failure: keep the server-rendered collection fallback.
      });
  };

  const initSection = (section) => {
    if (!section || section.dataset[initialized] === 'true') return;

    const track = section.querySelector(trackSelector);
    const previousButton = section.querySelector(previousSelector);
    const nextButton = section.querySelector(nextSelector);
    const scrollbarThumb = section.querySelector(scrollbarThumbSelector);

    if (!track) return;

    section.dataset[initialized] = 'true';

    let frame = 0;
    let resizeObserver = null;
    const scheduleUpdate = () => {
      if (frame) return;

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateButtonState(track, previousButton, nextButton);
        updateScrollbarThumb(track, scrollbarThumb);
      });
    };

    const scrollPrevious = () => {
      track.scrollBy({
        left: getScrollAmount(track) * -1,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    };

    const scrollNext = () => {
      track.scrollBy({
        left: getScrollAmount(track),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    };

    previousButton?.addEventListener('click', scrollPrevious);
    nextButton?.addEventListener('click', scrollNext);
    track.addEventListener('scroll', scheduleUpdate, { passive: true });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', scheduleUpdate, { passive: true });
    }

    section.bestSellersCleanup = () => {
      if (frame) window.cancelAnimationFrame(frame);
      previousButton?.removeEventListener('click', scrollPrevious);
      nextButton?.removeEventListener('click', scrollNext);
      track.removeEventListener('scroll', scheduleUpdate);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      delete section.dataset[initialized];
    };

    scheduleUpdate();

    loadRecommendations(section, track, () => {
      track.scrollLeft = 0;
      scheduleUpdate();
    });
  };

  const initBestSellers = (root = document) => {
    if (root.matches?.(sectionSelector)) initSection(root);
    root.querySelectorAll?.(sectionSelector).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initBestSellers(), { once: true });
  } else {
    initBestSellers();
  }

  document.addEventListener('shopify:section:load', (event) => initBestSellers(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(sectionSelector)
      ? event.target
      : event.target.querySelector?.(sectionSelector);
    section?.bestSellersCleanup?.();
  });
})();
